import { useCallback, useEffect, useState } from 'react'

export type ThemePreference = 'light' | 'dark' | 'auto'

const KEY = 'lb:theme'
const DARK_QUERY = '(prefers-color-scheme: dark)'

/** The colours `styles.css` paints the browser chrome with, per theme. */
const CHROME = { light: '#fdfaf4', dark: '#15120d' } as const

export function readPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(KEY)
    return stored === 'light' || stored === 'dark' ? stored : 'auto'
  } catch {
    // Private mode: the choice just won't survive a reload.
    return 'auto'
  }
}

const systemIsDark = () => window.matchMedia(DARK_QUERY).matches

export const resolveTheme = (preference: ThemePreference): 'light' | 'dark' =>
  preference === 'auto' ? (systemIsDark() ? 'dark' : 'light') : preference

/**
 * Write the resolved theme where the CSS can see it.
 *
 * `data-theme` is always set, never absent -- "auto" is resolved here rather
 * than left to a media query, so every dark rule can be written once against
 * one attribute instead of twice.
 */
export function applyTheme(resolved: 'light' | 'dark') {
  document.documentElement.dataset.theme = resolved
  // The address bar and status bar are painted by the browser, not the page.
  for (const meta of document.querySelectorAll('meta[name="theme-color"]')) {
    meta.setAttribute('content', CHROME[resolved])
  }
}

export interface Theme {
  preference: ThemePreference
  /** What `preference` currently works out to. */
  resolved: 'light' | 'dark'
  setPreference: (next: ThemePreference) => void
}

export function useTheme(): Theme {
  const [preference, setStored] = useState<ThemePreference>(readPreference)
  const [resolved, setResolved] = useState<'light' | 'dark'>(() => resolveTheme(readPreference()))

  useEffect(() => {
    const next = resolveTheme(preference)
    setResolved(next)
    applyTheme(next)
  }, [preference])

  /* On "auto", follow the system if it flips while the app is open -- which is
     exactly what happens on a tablet with a scheduled night mode. */
  useEffect(() => {
    if (preference !== 'auto') return
    const query = window.matchMedia(DARK_QUERY)
    const onChange = () => {
      const next = systemIsDark() ? 'dark' : 'light'
      setResolved(next)
      applyTheme(next)
    }
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [preference])

  const setPreference = useCallback((next: ThemePreference) => {
    setStored(next)
    try {
      if (next === 'auto') localStorage.removeItem(KEY)
      else localStorage.setItem(KEY, next)
    } catch {
      // Private mode again: it applies now, it just won't be remembered.
    }
  }, [])

  return { preference, resolved, setPreference }
}
